// Figma: 7793:62165 — see stories/blocks/bento-marquee-row.stories.tsx
"use client";

import { CareersBentoCta } from "@/components/about-us/careers-bento-cta";
import { BentoWayConShowreel } from "@/components/blocks/bento-waycon-showreel";
import { MarqueeTrack } from "@/components/blocks/marquee-track";
import { StatCounter } from "@/components/blocks/stat-counter";
import { Link } from "@/components/ui/link";
import type { BentoWayConShowreelImage } from "@/content/shared/about-us/bento-waycon-showreel";
import { cn } from "@/lib/utils";

export type BentoMarqueeCard =
  | {
      kind: "image";
      src: string;
      alt: string;
      width: "wide" | "narrow";
      caption?: string;
      captionPosition?: "top-left" | "bottom-left";
    }
  | {
      kind: "showreel";
      images: readonly BentoWayConShowreelImage[];
      width: "wide" | "narrow";
    }
  | {
      kind: "stat";
      label: string;
      width: "wide" | "narrow";
      startValue: number;
      endValue: number;
      liveSuffix: string;
      locale?: string;
    }
  | {
      kind: "cta";
      title: string;
      ctaLabel: string;
      ctaHref: string;
      width: "wide" | "narrow";
    };

interface BentoMarqueeRowProps {
  cards: BentoMarqueeCard[];
  direction: "ltr" | "rtl";
  animationClassName: string;
}

export const BENTO_CARD_WIDTHS = {
  wide: "w-[300px] lg:w-[440px]",
  narrow: "w-[180px] lg:w-[260px]",
} as const;

/** Figma 5277-19047 column widths on the 1280px rail — drives aspect + cqw scaling. */
export type BentoGridColumnRef = "628" | "332" | "272";

const GRID_TILE_ASPECT: Record<BentoGridColumnRef, string> = {
  "628": "aspect-[628/292]",
  "332": "aspect-[332/292]",
  "272": "aspect-[272/292]",
};

const gridTileContainerStyle = { containerType: "size" } as const;

/** Grid overlay type — scales with tile width/height; clamped for tasteful md sizes. */
const GRID_CAPTION_BOTTOM =
  "max-w-[79.217cqw] text-[clamp(14px,min(7.5cqw,9.5cqh),28px)] leading-[1.2] tracking-[-0.04em] lg:text-[clamp(18px,min(9.639cqw,10.959cqh),32px)]";
const GRID_CAPTION_TOP =
  "max-w-[37.899cqw] text-[clamp(11px,min(2.5cqw,5.5cqh),17px)] leading-[1.3] tracking-[-0.04em] lg:text-[clamp(12px,min(3.185cqw,6.849cqh),20px)]";
const GRID_STAT_VALUE =
  "text-[clamp(16px,min(11cqw,12cqh),32px)] leading-[1.2] tracking-[-0.04em] lg:text-[clamp(18px,min(14.706cqw,13.699cqh),40px)]";
const GRID_STAT_LABEL =
  "text-[clamp(11px,min(5.5cqw,5.5cqh),16px)] leading-[1.3] tracking-[-0.04em] lg:text-[clamp(11px,min(7.353cqw,6.849cqh),20px)]";

function cardShellClass(
  layout: "marquee" | "grid",
  widthClass: string,
  className?: string,
  options?: { allowOverflow?: boolean; gridColumnRef?: BentoGridColumnRef }
) {
  return cn(
    "rounded-ds-l border-grey-100 relative shrink-0 border",
    options?.allowOverflow ? "overflow-visible" : "overflow-hidden",
    layout === "grid" && options?.gridColumnRef
      ? cn("w-full min-w-0", GRID_TILE_ASPECT[options.gridColumnRef])
      : layout === "grid"
        ? "aspect-[272/292] w-full min-w-0"
        : cn("h-40", widthClass),
    className
  );
}

export function BentoMarqueeRow({
  cards,
  direction,
  animationClassName,
}: BentoMarqueeRowProps) {
  const loop = [...cards, ...cards];

  return (
    <MarqueeTrack
      direction={direction}
      animationClassName={animationClassName}
      trackClassName="gap-4 pr-4"
    >
      {loop.map((card, index) => {
        const isDuplicate = index >= cards.length;
        return (
          <li key={index} aria-hidden={isDuplicate} className="shrink-0">
            <BentoCard card={card} inert={isDuplicate} />
          </li>
        );
      })}
    </MarqueeTrack>
  );
}

function ObsessedCaption({ caption }: { caption: string }) {
  const match = caption.match(/^(Obsessed with)\s+(great businesses\.?)$/i);
  if (match) {
    return (
      <>
        <span className="block">{match[1]}</span>
        <span className="block">{match[2]}</span>
      </>
    );
  }
  return caption;
}

export function BentoCard({
  card,
  inert = false,
  layout = "marquee",
  gridColumnRef,
  className,
}: {
  card: BentoMarqueeCard;
  inert?: boolean;
  layout?: "marquee" | "grid";
  gridColumnRef?: BentoGridColumnRef;
  className?: string;
}) {
  const widthClass =
    layout === "grid" ? "w-full" : BENTO_CARD_WIDTHS[card.width];
  const gridShell = {
    gridColumnRef,
    style: layout === "grid" ? gridTileContainerStyle : undefined,
  };

  if (card.kind === "stat") {
    return (
      <div
        style={gridShell.style}
        className={cn(
          cardShellClass(layout, widthClass, className, gridShell),
          "bg-pulse-700 text-grey-0"
        )}
      >
        {/* Figma 5198-44077 — block centered, 62.67% from top in 292px tile. */}
        <div
          className={cn(
            "absolute left-1/2 z-20 flex -translate-x-1/2 flex-col items-start",
            layout === "grid"
              ? "top-[62.67%] w-[calc(100%-13.235cqw)] max-w-[73.529cqw] gap-[2.941cqw]"
              : "bottom-4 w-[calc(100%-28px)] max-w-[200px] gap-1"
          )}
        >
          <p
            className={cn(
              "text-grey-0 whitespace-nowrap",
              layout === "grid" ? GRID_STAT_VALUE : "text-[24px] leading-[29px]"
            )}
          >
            <span className="inline-flex items-baseline whitespace-nowrap">
              <StatCounter
                startValue={card.startValue}
                value={card.endValue}
                locale={card.locale}
                durationMs={600}
                className="text-grey-0"
              />
              {card.liveSuffix}
            </span>
          </p>
          <p
            className={cn(
              "w-full",
              layout === "grid"
                ? GRID_STAT_LABEL
                : "text-ds-body-xs leading-[1.4]"
            )}
          >
            {card.label}
          </p>
        </div>
      </div>
    );
  }

  if (card.kind === "showreel") {
    return (
      <div
        style={gridShell.style}
        className={cn(
          cardShellClass(layout, widthClass, className, gridShell),
          "bg-grey-200"
        )}
      >
        <BentoWayConShowreel images={card.images} />
      </div>
    );
  }

  if (card.kind === "cta") {
    return (
      <Link
        href={card.ctaHref}
        tabIndex={inert ? -1 : undefined}
        style={gridShell.style}
        className={cn(
          cardShellClass(layout, widthClass, className, {
            allowOverflow: true,
            gridColumnRef,
          }),
          "bg-pulse-50 text-grey-950 block no-underline"
        )}
      >
        <div
          className={cn(
            layout === "grid"
              ? "absolute top-[42.47%] bottom-[32%] left-1/2 flex w-[80.882cqw] max-w-[80.882cqw] -translate-x-1/2 flex-col gap-[5.882cqw] lg:bottom-[13.7%]"
              : "flex h-full flex-col items-start justify-center gap-2 px-4"
          )}
        >
          <p
            className={cn(
              "leading-[1.2] tracking-[-0.04em]",
              layout === "grid"
                ? "text-[8.824cqw]"
                : "text-ds-body-sm max-w-[120px]"
            )}
          >
            {card.title}
          </p>
          <div
            className={cn(
              layout === "grid" && "mt-auto shrink-0 pb-[4cqw] lg:pb-0"
            )}
          >
            <CareersBentoCta
              label={card.ctaLabel}
              scaled={layout === "grid"}
              size="sm"
            />
          </div>
        </div>
      </Link>
    );
  }

  const captionSizeClass =
    layout === "grid" && card.captionPosition === "bottom-left"
      ? gridColumnRef === "332"
        ? GRID_CAPTION_BOTTOM
        : "text-ds-heading-h3 max-w-[263px] leading-[1.2] tracking-[-0.04em]"
      : layout === "grid" && card.captionPosition === "top-left"
        ? gridColumnRef === "628"
          ? GRID_CAPTION_TOP
          : "text-ds-heading-h5 max-w-[238px] leading-[1.3] tracking-[-0.04em]"
        : card.captionPosition === "bottom-left"
          ? "text-ds-body-sm max-w-[85%] leading-[1.3] tracking-[-0.02em]"
          : "text-ds-body-xs max-w-[85%] leading-[1.4] tracking-[-0.02em]";

  return (
    <div
      style={gridShell.style}
      className={cn(
        cardShellClass(layout, widthClass, className, gridShell),
        "bg-grey-200"
      )}
    >
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        src={card.src}
        alt={inert ? "" : card.alt}
        className="absolute inset-0 size-full object-cover"
        loading="eager"
        decoding="async"
        fetchPriority="high"
      />
      {card.caption ? (
        <>
          <span
            aria-hidden
            className={cn(
              "pointer-events-none absolute inset-0 z-10",
              card.captionPosition === "top-left"
                ? "bg-gradient-to-b from-black/60 via-black/15 to-transparent"
                : "bg-gradient-to-t from-black/65 via-black/20 to-transparent"
            )}
          />
          <p
            className={cn(
              "text-grey-0 absolute z-20",
              captionSizeClass,
              card.captionPosition === "top-left"
                ? layout === "grid"
                  ? "top-[10.27%] left-[4.777cqw]"
                  : "top-[30px] left-[30px]"
                : layout === "grid"
                  ? "bottom-[8.22%] left-[7.229cqw]"
                  : "bottom-6 left-6"
            )}
          >
            {card.captionPosition === "bottom-left" ? (
              <ObsessedCaption caption={card.caption} />
            ) : (
              card.caption
            )}
          </p>
        </>
      ) : null}
    </div>
  );
}
